import type { Breadcrumb, Event } from '@sentry/react';

// Invitation links carry a usable JWT in `?invite=` (/login, /signup, and inside
// an encoded `next`). Sentry copies the page URL into every event's
// `request.url` and every navigation into a breadcrumb, so a capture on one of
// those pages would ship the token. ENG-6105.
export const FILTERED = '[Filtered]';

// `invite=` at the start of a query string or after `?`/`&`, value up to the
// next `&` or `#`.
const RAW_INVITE = /((?:^|[?&])invite=)[^&#]*/gi;

// The same parameter inside a percent-encoded URL (a `next=` value), encoded
// once or more: `%3F`/`%26` before it, `%3D` after the name, value up to the
// next encoded `&`, a literal `&` or `#`.
const ENCODED_INVITE = /((?:%(?:25)*3F|%(?:25)*26)invite%(?:25)*3D)(?:(?!%(?:25)*26)[^&#])*/gi;

/** Replace the value of every `invite` query parameter in a URL or query string. */
export function redactInviteInUrl(url: string): string {
  if (!/invite/i.test(url)) return url;
  return url.replace(RAW_INVITE, `$1${FILTERED}`).replace(ENCODED_INVITE, `$1${FILTERED}`);
}

function redactValue(key: string, value: string): string {
  return key.toLowerCase() === 'invite' ? FILTERED : redactInviteInUrl(value);
}

type QueryParams = NonNullable<NonNullable<Event['request']>['query_string']>;

function redactQueryString(query: QueryParams): QueryParams {
  if (typeof query === 'string') return redactInviteInUrl(query);
  if (Array.isArray(query)) {
    return query.map(([key, value]) => [key, redactValue(key, value)] as [string, string]);
  }
  return Object.fromEntries(
    Object.entries(query).map(([key, value]) => [key, redactValue(key, value)])
  );
}

const BREADCRUMB_URL_KEYS = ['from', 'to', 'url'] as const;

/** Breadcrumb with `invite` values redacted from `data.from`, `data.to` and `data.url`. */
export function redactInviteInBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;
  let next: typeof data | undefined;
  for (const key of BREADCRUMB_URL_KEYS) {
    const value = data[key];
    if (typeof value !== 'string') continue;
    const redacted = redactInviteInUrl(value);
    if (redacted === value) continue;
    next ??= { ...data };
    next[key] = redacted;
  }
  return next ? { ...breadcrumb, data: next } : breadcrumb;
}

type DataRecord = Record<string, unknown>;

/** `data` with every string value passed through redactInviteInUrl; same object if unchanged. */
function redactStringValues<D extends DataRecord>(data: D): D {
  let next: DataRecord | undefined;
  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== 'string') continue;
    const redacted = redactInviteInUrl(value);
    if (redacted === value) continue;
    next ??= { ...data };
    next[key] = redacted;
  }
  return (next as D | undefined) ?? data;
}

type ReduxStateWithReferrer = { runtime?: { referrer?: unknown } };

/**
 * Redux state with the invite value redacted from `runtime.referrer` (set from
 * `document.referrer`, which after a redirect from an invitation link holds the
 * token). Same object when nothing matches, so every other slice is untouched.
 */
export function redactInviteInReduxState<S>(state: S): S {
  const runtime = (state as ReduxStateWithReferrer | null | undefined)?.runtime;
  const referrer = runtime?.referrer;
  if (typeof referrer !== 'string') return state;
  const redacted = redactInviteInUrl(referrer);
  if (redacted === referrer) return state;
  return { ...state, runtime: { ...runtime, referrer: redacted } };
}

type Contexts = NonNullable<Event['contexts']>;

function redactContexts(contexts: Contexts): Contexts {
  let next = contexts;
  // Pageload/navigation spans carry the URL in trace data (`url.query`,
  // `url.full`, `http.query`, ...).
  const traceData = contexts.trace?.data;
  if (traceData) {
    const redacted = redactStringValues(traceData);
    if (redacted !== traceData) next = { ...next, trace: { ...contexts.trace!, data: redacted } };
  }
  // The Sentry Redux enhancer's snapshot: { state: { type: 'redux', value } }.
  const stateContext = contexts.state;
  if (stateContext?.state?.type === 'redux') {
    const value = redactInviteInReduxState(stateContext.state.value);
    if (value !== stateContext.state.value) {
      next = { ...next, state: { ...stateContext, state: { ...stateContext.state, value } } };
    }
  }
  return next;
}

/**
 * Event with `invite` values redacted from its request, breadcrumbs, trace
 * data, span data and Redux state context.
 */
export function redactInviteInEvent<E extends Event>(event: E): E {
  let result = event;
  const request = event.request;
  if (request) {
    const nextRequest = { ...request };
    if (typeof request.url === 'string') nextRequest.url = redactInviteInUrl(request.url);
    if (request.query_string) nextRequest.query_string = redactQueryString(request.query_string);
    if (request.headers?.Referer) {
      nextRequest.headers = {
        ...request.headers,
        Referer: redactInviteInUrl(request.headers.Referer),
      };
    }
    result = { ...result, request: nextRequest };
  }
  if (Array.isArray(event.breadcrumbs)) {
    result = { ...result, breadcrumbs: event.breadcrumbs.map(redactInviteInBreadcrumb) };
  }
  if (event.contexts) {
    const contexts = redactContexts(event.contexts);
    if (contexts !== event.contexts) result = { ...result, contexts };
  }
  if (Array.isArray(event.spans)) {
    result = {
      ...result,
      spans: event.spans.map((span) => {
        if (!span.data) return span;
        const data = redactStringValues(span.data);
        return data === span.data ? span : { ...span, data };
      }),
    };
  }
  return result;
}

type ReplayRecordingEvent = { data?: { tag?: unknown; payload?: unknown } | null };

type ReplaySpanPayload = { description?: unknown; data?: unknown };

function redactSpanPayload(payload: ReplaySpanPayload): ReplaySpanPayload {
  const { description, data } = payload;
  const nextDescription =
    typeof description === 'string' ? redactInviteInUrl(description) : description;
  const nextData = data && typeof data === 'object' ? redactStringValues(data as DataRecord) : data;
  if (nextDescription === description && nextData === data) return payload;
  return { ...payload, description: nextDescription, data: nextData };
}

/**
 * Session Replay recording event with `invite` values redacted from a performance
 * span's `description` (the page or request URL) and string `data` (`previous`, the
 * URL a history navigation left), and from a breadcrumb frame's `data.url` (slow and
 * multi clicks, which replay records itself, past beforeBreadcrumb). Same object
 * when nothing matches. ENG-6160.
 */
export function redactInviteInReplayRecordingEvent<R>(event: R): R {
  const data = (event as ReplayRecordingEvent | null)?.data;
  if (!data?.payload) return event;
  let payload = data.payload;
  if (data.tag === 'performanceSpan') payload = redactSpanPayload(payload as ReplaySpanPayload);
  if (data.tag === 'breadcrumb') payload = redactInviteInBreadcrumb(payload as Breadcrumb);
  return payload === data.payload ? event : { ...event, data: { ...data, payload } };
}

/**
 * Replay event with `invite` values redacted from its `urls` list (every page the
 * segment visited) as well as everything redactInviteInEvent covers.
 */
export function redactInviteInReplayEvent<E extends Event>(event: E): E {
  const result = redactInviteInEvent(event);
  const urls = (result as { urls?: unknown }).urls;
  if (!Array.isArray(urls)) return result;
  return {
    ...result,
    urls: urls.map((url: unknown) => (typeof url === 'string' ? redactInviteInUrl(url) : url)),
  };
}
